import { fetchAuthSession } from 'aws-amplify/auth';
import type {
  OverviewResponse,
  ChatListResponse,
  ChatDetail,
  DashboardSeriesResponse,
  DashboardSeriesRange,
  OkResponse,
  Ticket,
  TicketListResponse,
  TicketReplyResponse,
  TicketStatus,
  AdminGscSummary
} from '../types';

const API_URL = (import.meta.env.VITE_API_URL as string) || '';

const DEV_AUTH_BYPASS =
  import.meta.env.DEV && import.meta.env.VITE_DEV_AUTH === '1';

async function getAuthToken(): Promise<string | null> {
  if (DEV_AUTH_BYPASS) return null;
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  // If the build shipped without VITE_API_URL set, every fetch lands on the
  // SPA's own origin, which CloudFront SPA-rewrites back to /index.html.
  // Surface that as a clear message instead of a JSON.parse error.
  if (!API_URL) {
    throw new Error(
      'Admin API is not configured. The deploy pipeline did not inject ' +
      'VITE_API_URL — wait for the next deploy or rebuild the SPA after ' +
      'the XBotAdminApi stack lands.'
    );
  }

  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_URL}${path}`, { ...options, headers });

  // Try to parse JSON regardless of status — most error paths still return
  // JSON. If the response is HTML / empty / truncated we surface a clear
  // message rather than the cryptic JSON.parse error.
  const rawText = await response.text();
  let body: unknown = null;
  if (rawText) {
    try {
      body = JSON.parse(rawText);
    } catch {
      throw new Error(
        `API returned non-JSON (${response.status} ${response.statusText}). ` +
        `First 80 chars: ${rawText.slice(0, 80)}`
      );
    }
  }

  if (!response.ok) {
    const message =
      (body as { error?: string } | null)?.error ||
      response.statusText ||
      `HTTP ${response.status}`;
    throw new Error(message);
  }

  return body as T;
}

export function getOverview(): Promise<OverviewResponse> {
  return apiRequest<OverviewResponse>('/admin/overview');
}

export function getDashboardSeries(range: DashboardSeriesRange): Promise<DashboardSeriesResponse> {
  return apiRequest<DashboardSeriesResponse>(`/admin/dashboard-series?range=${encodeURIComponent(range)}`);
}

export interface ListChatsParams {
  tier?: string;
  status?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

export function listChats(params: ListChatsParams = {}): Promise<ChatListResponse> {
  const searchParams = new URLSearchParams();
  if (params.tier) searchParams.set('tier', params.tier);
  if (params.status) searchParams.set('status', params.status);
  if (params.search) searchParams.set('search', params.search);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.cursor) searchParams.set('cursor', params.cursor);
  const qs = searchParams.toString();
  return apiRequest<ChatListResponse>(`/admin/chats${qs ? `?${qs}` : ''}`);
}

export function getChat(chatId: string): Promise<ChatDetail> {
  return apiRequest<ChatDetail>(`/admin/chats/${encodeURIComponent(chatId)}`);
}

export interface DeleteChatPreflight {
  chatId: string;
  hasSchedule: boolean;
  botMembership: string | null;
  botCanPost: boolean | null;
  canSelfLeave: boolean;
  warnings: { severity: 'block' | 'warn'; code: string; message: string }[];
}

export function getDeleteChatPreflight(chatId: string): Promise<DeleteChatPreflight> {
  return apiRequest<DeleteChatPreflight>(
    `/admin/chats/${encodeURIComponent(chatId)}/delete-preflight`
  );
}

export interface DeleteChatSummary {
  chatId: string;
  startedAt: number;
  completedAt: number;
  elapsedMs: number;
  steps: Record<string, unknown>;
  errors: { step: string; error: string }[];
}

export function deleteChat(
  chatId: string,
  body: { confirmChatId: string; alsoLeaveChat: boolean }
): Promise<DeleteChatSummary> {
  return apiRequest<DeleteChatSummary>(`/admin/chats/${encodeURIComponent(chatId)}`, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export interface ReactivateChatResult {
  ok: boolean;
  cron?: string;
  hour?: number;
  minute?: number;
  period?: string;
  error?: string;
}

export function reactivateChat(chatId: string): Promise<ReactivateChatResult> {
  return apiRequest<ReactivateChatResult>(
    `/admin/chats/${encodeURIComponent(chatId)}/reactivate`,
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export interface RunReportResult {
  ok: boolean;
  executionArn?: string | null;
  error?: string;
}

export function runReport(chatId: string): Promise<RunReportResult> {
  return apiRequest<RunReportResult>(
    `/admin/chats/${encodeURIComponent(chatId)}/run-report`,
    { method: 'POST', body: JSON.stringify({}) }
  );
}

export interface InviteLinkResult {
  ok: boolean;
  inviteLink?: string;
  expiresAt?: number;
  error?: string;
}

export function generateInviteLink(chatId: string): Promise<InviteLinkResult> {
  return apiRequest<InviteLinkResult>(
    `/admin/chats/${encodeURIComponent(chatId)}/invite-link`
  );
}

// Action endpoints (Phase 2). Kept here so types stay in one place.
export function pauseChat(chatId: string): Promise<OkResponse> {
  return apiRequest<OkResponse>(`/admin/chats/${encodeURIComponent(chatId)}/pause`, {
    method: 'POST'
  });
}

export function resumeChat(chatId: string): Promise<OkResponse> {
  return apiRequest<OkResponse>(`/admin/chats/${encodeURIComponent(chatId)}/resume`, {
    method: 'POST'
  });
}

export function grantCredits(chatId: string, amount: number): Promise<OkResponse> {
  return apiRequest<OkResponse>(`/admin/chats/${encodeURIComponent(chatId)}/grant-credits`, {
    method: 'POST',
    body: JSON.stringify({ amount })
  });
}

// Tickets
export function listTickets(status: TicketStatus = 'open', limit = 50): Promise<TicketListResponse> {
  return apiRequest<TicketListResponse>(
    `/admin/tickets?status=${encodeURIComponent(status)}&limit=${limit}`
  );
}

export function getTicket(ticketId: string): Promise<Ticket> {
  return apiRequest<Ticket>(`/admin/tickets/${encodeURIComponent(ticketId)}`);
}

export function replyToTicket(
  ticketId: string,
  reply: string,
  status: TicketStatus = 'resolved'
): Promise<TicketReplyResponse> {
  return apiRequest<TicketReplyResponse>(
    `/admin/tickets/${encodeURIComponent(ticketId)}/reply`,
    {
      method: 'POST',
      body: JSON.stringify({ reply, status })
    }
  );
}

export interface TicketStatusResponse {
  success: boolean;
  ticketId: string;
  status: TicketStatus;
  notified: boolean;
}

/**
 * Change a ticket's status WITHOUT contacting the customer.
 *
 * replyToTicket always sends a Telegram DM before it writes, so using it to
 * tidy an old backlog re-pings people about tickets they had long forgotten.
 * Use this for housekeeping; use replyToTicket when the customer should
 * actually read something.
 */
export function setTicketStatus(
  ticketId: string,
  status: TicketStatus
): Promise<TicketStatusResponse> {
  return apiRequest<TicketStatusResponse>(
    `/admin/tickets/${encodeURIComponent(ticketId)}/status`,
    {
      method: 'PATCH',
      body: JSON.stringify({ status })
    }
  );
}

// ---- Growth / conversion (Behavior, Anomalies, Metrics) ----

export interface SegmentChat {
  chatId: string;
  segment: string;
  reason: string;
  reportCount: number | null;
  monthlyPosts: number | null;
  license: string | null;
  lastReportTs: number | null;
  updatedAt: string | null;
}
export interface SegmentsResponse {
  total: number;
  counts: Record<string, number>;
  order: string[];
  chats: SegmentChat[];
}
export interface AnomalyEvent {
  chatId: string;
  type: string;
  ts: number;
  payload: Record<string, unknown> | null;
}
export interface AnomaliesResponse {
  sinceMs: number;
  days: number;
  events: AnomalyEvent[];
  topChats: { chatId: string; count: number }[];
  total: number;
}
export interface ConversionMetricsResponse {
  rangeDays: number;
  installs: number;
  upgrades: number;
  reportsSent: number;
  segments: Record<string, number>;
  totalTracked: number;
  activatedApproxPct: number;
  series: { installs: { date: string; count: number }[]; activeChats: { date: string; count: number }[]; upgrades: { date: string; count: number }[] };
}
export interface ChatJourneyResponse {
  chatId: string;
  segment: { segment: string; reason: string } | null;
  events: { type: string; ts: number; payload: Record<string, unknown> | null; userId: string | null }[];
}

export function getSegments(): Promise<SegmentsResponse> {
  return apiRequest<SegmentsResponse>('/admin/segments');
}
export function getAnomalies(range = '30d'): Promise<AnomaliesResponse> {
  return apiRequest<AnomaliesResponse>(`/admin/anomalies?range=${encodeURIComponent(range)}`);
}
export function getConversionMetrics(range = '90d'): Promise<ConversionMetricsResponse> {
  return apiRequest<ConversionMetricsResponse>(`/admin/conversion-metrics?range=${encodeURIComponent(range)}`);
}
export function getChatJourney(chatId: string): Promise<ChatJourneyResponse> {
  return apiRequest<ChatJourneyResponse>(`/admin/chats/${encodeURIComponent(chatId)}/journey`);
}

// ---- Bot Messages — outreach templates (Phase 4) ----

export type OutreachButton =
  | { kind: 'cta'; text: string; action: 'setup' | 'report' | 'buy' }
  | { kind: 'help'; text: string }
  | { kind: 'survey'; text: string; option: string };

export interface OutreachTemplate {
  key: string;
  label: string;
  title: string;
  bodyHtml: string;
  buttons: OutreachButton[];
  enabled: boolean;
  upsell: boolean;
  source: 'default' | 'override';
}
export interface MessageTemplatesResponse {
  masterEnabled: boolean;
  templates: OutreachTemplate[];
}
export interface SaveTemplateResponse {
  ok: boolean;
  template: OutreachTemplate;
  savedAt: string;
}
export interface SendOutreachResponse {
  ok: boolean;
  sent?: boolean;
  dryRun?: boolean;
  skip?: string;
  target?: string;
  error?: string;
}

export function getMessageTemplates(): Promise<MessageTemplatesResponse> {
  return apiRequest<MessageTemplatesResponse>('/admin/message-templates');
}
export function saveMessageTemplate(
  key: string,
  fields: { title: string; bodyHtml: string; buttons: OutreachButton[]; enabled: boolean; upsell: boolean }
): Promise<SaveTemplateResponse> {
  return apiRequest<SaveTemplateResponse>(`/admin/message-templates/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(fields)
  });
}
export function sendOutreach(chatId: string, key: string): Promise<SendOutreachResponse> {
  return apiRequest<SendOutreachResponse>(`/admin/chats/${encodeURIComponent(chatId)}/outreach`, {
    method: 'POST',
    body: JSON.stringify({ key })
  });
}

// ---- Google Search Console ----
export function getGscSummary(): Promise<AdminGscSummary> {
  return apiRequest<AdminGscSummary>('/admin/gsc');
}
